import { eventGetters } from '~/utils/eventGetters'

/** Maps i18n locale (e.g. `de-de`) to Algolia record keys (e.g. `de_DE`). */
export const localeToAlgoliaNameKey = (locale: string): string => {
  const normalized = locale.replace(/_/g, '-').toLowerCase()
  const parts = normalized.split('-')
  const lang = parts[0] || 'de'
  const region = (parts[1] || lang).toUpperCase()
  return `${lang}_${region}`
}

/** Display name for `locations` index hits (`name` may be a per-locale object). */
export const getLocationDisplayName = (item: any, locale: string): string => {
  const name = item?.name
  if (typeof name === 'string') return name
  if (name && typeof name === 'object') {
    const key = localeToAlgoliaNameKey(locale)
    if (typeof name[key] === 'string') return name[key]
    const first = Object.values(name).find((v) => typeof v === 'string')
    return (first as string) || ''
  }
  return ''
}

export const getLocationNameAttributeForAlgolia = (locale: string): string =>
  `name.${localeToAlgoliaNameKey(locale)}`

export const GEO_PRECISION = [
  { from: 0, value: 15000 },
  { from: 30000, value: 30000 },
  { from: 150000, value: 100000 },
] as const

export const HITS_PER_PAGE = {
  locations: { mobile: 3, desktop: 6 },
  categories: 3,
  events: 8,
  lastViewed: 4,
} as const

export const getProductPath = (item: any) => {
  if (item?.supplier === 'konfetti Gutscheine') {
    return `/giftcard/${item.permalink}`
  }
  if (eventGetters.isBoxEvent(item)) {
    return `/k/${item.permalink}`
  }
  return `/e/${item.permalink}`
}

/**
 * Builds the events-column list, always reserving at least one slot for a
 * kit hit when kits are available — even if events alone would fill
 * `maxItems` — and otherwise filling any remaining slots with kits.
 */
export const mergeEventsWithKits = (
  eventHits: any[],
  kitHits: any[],
  maxItems: number
): any[] => {
  if (kitHits.length === 0) return eventHits.slice(0, maxItems)
  const eventsToShow = eventHits.slice(0, maxItems - 1)
  const remainingSlots = maxItems - eventsToShow.length
  return [...eventsToShow, ...kitHits.slice(0, remainingSlots)]
}

export const mountUrlParamsFromItem = (item: any) => {
  const location = 'location=' + encodeURIComponent(item.slug)
  const rawType = item?.type
  const locationType = rawType
    ? 'locationType=' + encodeURIComponent(rawType)
    : ''
  return [location, locationType].filter(Boolean).join('&')
}
